import React, { useCallback, useEffect, useState } from "react";
import { Mail, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function EmailPreferencesPage() {
  const { user } = useAuth();
  const [optIn, setOptIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/marketing/me");
      setOptIn(Boolean(res?.data?.marketing_email_opt_in));
    } catch (_e) {
      toast.error("Could not load your email preferences.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onToggle = async (next) => {
    setSaving(true);
    const prev = optIn;
    setOptIn(next);
    try {
      await api.post("/marketing/me", { marketing_email_opt_in: Boolean(next) });
      toast.success(
        next
          ? "Subscribed to Postmoro product updates."
          : "Unsubscribed from marketing emails.",
      );
    } catch (_e) {
      setOptIn(prev);
      toast.error("Could not update your preference. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <GradientMesh />
      <div className="space-y-6 pb-10" data-testid="email-preferences-page">
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Mail className="h-5 w-5 text-[color:var(--brand-primary)]" />
              Email Preferences
            </span>
          }
          subtitle="Choose what optional email you receive from Postmoro."
        />

        <GlassCard className="p-5" data-testid="marketing-pref-card">
          <div className="flex items-start justify-between gap-4 flex-wrap sm:flex-nowrap">
            <div className="min-w-0">
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">
                Marketing &amp; product-update emails
              </div>
              <p className="text-sm text-[color:var(--text-2)] mt-1 leading-relaxed">
                Receive Postmoro product updates, new-feature announcements,
                helpful tips, and occasional Postmoro offers by email.
              </p>
              <p className="text-xs text-[color:var(--text-3)] mt-2 leading-relaxed">
                Turning this off <strong>only</strong> stops marketing email.
                Transactional messages (receipts, password reset, security
                notices, and support-ticket replies) are separate and are
                not affected by this toggle.
              </p>
              <p className="text-[11px] text-[color:var(--text-3)] mt-2 font-mono truncate">
                {user?.email}
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-2">
              {loading || saving ? (
                <Loader2 className="h-4 w-4 animate-spin text-[color:var(--text-3)]" />
              ) : null}
              <Switch
                checked={optIn}
                disabled={loading || saving}
                onCheckedChange={onToggle}
                aria-label="Toggle marketing emails"
                data-testid="marketing-pref-toggle"
              />
            </div>
          </div>
        </GlassCard>

        <div
          className="text-[11px] text-[color:var(--text-3)] leading-relaxed max-w-2xl"
          data-testid="email-preferences-footnote"
        >
          You can also unsubscribe with a single click from the footer of any
          Postmoro marketing email.
        </div>
      </div>
    </>
  );
}
